// Wallet send flow — /vault/send?currency=TRX_USDT
//
// Two steps in one route (mirrors the topup pattern):
//   Step 1: amount entry (custom keypad) + recipient address (paste)
//   Step 2: confirmation (fee deducted automatically), then execute send
//
// Back from step 2 → step 1 (preserves amount + address)
// Back from step 1 → /vault?currency=TRX_USDT (detail view)

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { getWalletBalances, sendWallet, type WalletBalancesResponse } from '../api/client.ts';
import { STEP_UP_ACTIONS } from '../api/step-up.ts';
import { IconChevronLeft } from '../components/icons.tsx';
import { showToast } from '../components/toast.tsx';
import { applyPendingSends, registerPendingSend } from '../lib/pending-sends.ts';
import { isStepUpCancelled, useStepUpGate } from '../lib/use-step-up.tsx';

import { rootRoute } from './__root.tsx';

export interface WalletSendSearch {
  readonly currency: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const currencyMeta = (
  currency: string,
): { readonly name: string; readonly network: string; readonly token: string } => {
  const map: Record<string, { name: string; network: string; token: string }> = {
    TRX: { name: 'Tron', network: 'Tron', token: 'TRX' },
    TRX_USDT: { name: 'Tether USD', network: 'Tron', token: 'USDT' },
    SOL_USDT: { name: 'Tether USD', network: 'Solana', token: 'USDT' },
    ETH_USDT: { name: 'Tether USD', network: 'Ethereum', token: 'USDT' },
    ETH_USDC: { name: 'USD Coin', network: 'Ethereum', token: 'USDC' },
    SOL_USDC: { name: 'USD Coin', network: 'Solana', token: 'USDC' },
  };
  return map[currency] ?? { name: currency, network: '—', token: currency };
};

// Appends a digit or decimal point to the current amount string.
// Rules: single decimal point · max 9 significant chars · no leading zeros.
const applyKey = (current: string, key: string): string => {
  if (key === 'del') return current.slice(0, -1);
  if (key === '.' && current.includes('.')) return current;
  if (key === '.' && current === '') return '0.';
  if (current === '0' && key !== '.') return key;
  if (current.replace('.', '').length >= 9) return current;
  return current + key;
};

const isValidAmount = (v: string): boolean => {
  if (v === '' || v === '0' || v === '0.') return false;
  const n = Number(v);
  return Number.isFinite(n) && n > 0;
};

const isValidAddress = (v: string): boolean => v.trim().length >= 10;

// TRON network fees are paid in native TRX. On a native-TRX send we hold back a
// reserve so the wallet keeps enough TRX to cover the fee; token sends (USDT,
// USDC) draw fees from the separate TRX balance, so the full token balance is
// sendable. NOTE: this reserve is a UI guard, not a guarantee — an unstaked
// wallet's real energy cost can exceed it, so a send may still fail upstream.
const TRX_FEE_RESERVE = 5;
const isNativeTrx = (currency: string): boolean => currency === 'TRX';

// Max amount the user may send for a currency, given their available balance.
const maxSendable = (currency: string, available: string): number => {
  const bal = Number(available);
  if (!Number.isFinite(bal) || bal <= 0) return 0;
  return isNativeTrx(currency) ? Math.max(0, bal - TRX_FEE_RESERVE) : bal;
};

// Validates the amount against the per-currency max. Returns an error string to
// show inline, or null when the amount is acceptable.
const amountError = (currency: string, amount: string, available: string): string | null => {
  if (!isValidAmount(amount)) return null; // empty/zero: button stays disabled, no error yet
  const n = Number(amount);
  const max = maxSendable(currency, available);
  if (isNativeTrx(currency) && n > max) {
    return `Keep ${String(TRX_FEE_RESERVE)} TRX for network fees — max ${String(max)}`;
  }
  if (n > max) return 'Exceeds available balance';
  return null;
};

// Shorten an address for display.
const truncateAddress = (v: string, head = 10, tail = 8): string =>
  v.length > head + tail + 1 ? `${v.slice(0, head)}…${v.slice(-tail)}` : v;

// ─── Step 1: Amount entry + recipient address ─────────────────────────────────

const Step1 = ({
  currency,
  available,
  amount,
  address,
  onAmountKey,
  onPaste,
  onAddressChange,
  onNext,
  onBack,
}: {
  readonly currency: string;
  readonly available: string;
  readonly amount: string;
  readonly address: string;
  readonly onAmountKey: (k: string) => void;
  readonly onPaste: () => void;
  readonly onAddressChange: (v: string) => void;
  readonly onNext: () => void;
  readonly onBack: () => void;
}): ReactNode => {
  const meta = currencyMeta(currency);
  const amtErr = amountError(currency, amount, available);
  const canReview = isValidAmount(amount) && isValidAddress(address) && amtErr === null;
  const [intPart = '0', decPart] = amount.split('.');

  return (
    <main className="screen">
      <div className="topbar fade-in">
        <button type="button" className="topbar-back" aria-label="Back" onClick={onBack}>
          <IconChevronLeft />
        </button>
        <p className="eyebrow">Send {meta.token}</p>
        <span className="topbar-spacer" aria-hidden="true" />
      </div>

      {/* Amount hero with editable underline */}
      <div className="hero amount-input fade-in d1" style={{ paddingBottom: 20 }}>
        <p className="eyebrow hero-eyebrow">Amount</p>
        <div
          className="hero-amount"
          aria-label={`Amount: ${amount === '' ? '0' : amount} ${meta.token}`}
        >
          <span className="hero-int">{amount === '' ? '0' : intPart}</span>
          {decPart !== undefined ? <span className="hero-dec">.{decPart}</span> : null}
        </div>
        <div className="hero-focus-line" />
        <p className="hero-meta" style={{ marginTop: 10 }}>
          {meta.token} · {meta.network} · {available} available
        </p>
        {amtErr !== null ? (
          <p
            role="alert"
            style={{
              marginTop: 8,
              fontSize: 12,
              color: 'var(--danger, #e5484d)',
              textAlign: 'center',
            }}
          >
            {amtErr}
          </p>
        ) : null}
      </div>

      {/* Recipient address */}
      <div className="fade-in d2">
        <p
          style={{
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'var(--text-tertiary)',
            marginBottom: 9,
          }}
        >
          To address
        </p>
        <div className="wallet-address-input">
          <input
            type="text"
            className={`wallet-address-text${address !== '' ? ' filled' : ''}`}
            value={address}
            onChange={(e): void => {
              onAddressChange(e.target.value);
            }}
            placeholder="Paste or type recipient address"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              padding: 0,
              font: 'inherit',
              color: 'inherit',
            }}
          />
          <button
            type="button"
            className="wallet-paste-btn"
            onClick={onPaste}
            aria-label="Paste address from clipboard"
          >
            Paste
          </button>
        </div>
      </div>

      {/* Custom keypad — reuses pin-keypad CSS */}
      <div className="pin-keypad fade-in d3">
        <div className="pin-keypad-row">
          {(['1', '2', '3'] as const).map((k) => (
            <button
              key={k}
              type="button"
              className="pin-keypad-key"
              onClick={(): void => {
                onAmountKey(k);
              }}
            >
              {k}
            </button>
          ))}
        </div>
        <div className="pin-keypad-row">
          {(['4', '5', '6'] as const).map((k) => (
            <button
              key={k}
              type="button"
              className="pin-keypad-key"
              onClick={(): void => {
                onAmountKey(k);
              }}
            >
              {k}
            </button>
          ))}
        </div>
        <div className="pin-keypad-row">
          {(['7', '8', '9'] as const).map((k) => (
            <button
              key={k}
              type="button"
              className="pin-keypad-key"
              onClick={(): void => {
                onAmountKey(k);
              }}
            >
              {k}
            </button>
          ))}
        </div>
        <div className="pin-keypad-row">
          <button
            type="button"
            className="pin-keypad-key"
            onClick={(): void => {
              onAmountKey('.');
            }}
            aria-label="Decimal point"
          >
            ·
          </button>
          <button
            type="button"
            className="pin-keypad-key"
            onClick={(): void => {
              onAmountKey('0');
            }}
          >
            0
          </button>
          <button
            type="button"
            className="pin-keypad-key del"
            aria-label="Delete"
            onClick={(): void => {
              onAmountKey('del');
            }}
          >
            ⌫
          </button>
        </div>
      </div>

      <button
        type="button"
        className="btn-primary"
        style={{ marginTop: 14 }}
        disabled={!canReview}
        onClick={onNext}
      >
        Review Send
      </button>
    </main>
  );
};

// ─── Step 2: Confirmation ─────────────────────────────────────────────────────

const Step2 = ({
  currency,
  amount,
  address,
  balance,
  onConfirm,
  onBack,
  sending,
}: {
  readonly currency: string;
  readonly amount: string;
  readonly address: string;
  readonly balance: string;
  readonly onConfirm: () => void;
  readonly onBack: () => void;
  readonly sending: boolean;
}): ReactNode => {
  const meta = currencyMeta(currency);
  const remaining = Math.max(0, Number(balance) - Number(amount)).toFixed(6);
  const [intPart = '0', decPart] = amount.split('.');

  return (
    <main className="screen">
      <div className="topbar fade-in">
        <button
          type="button"
          className="topbar-back"
          aria-label="Back"
          onClick={onBack}
          disabled={sending}
        >
          <IconChevronLeft />
        </button>
        <p className="eyebrow">Confirm Send</p>
        <span className="topbar-spacer" aria-hidden="true" />
      </div>

      {/* Sending amount hero */}
      <div className="hero fade-in d1" style={{ paddingBottom: 28 }}>
        <p className="eyebrow hero-eyebrow">Sending</p>
        <div className="hero-amount">
          <span className="hero-int">{intPart}</span>
          {decPart !== undefined ? <span className="hero-dec">.{decPart}</span> : null}
        </div>
        <p className="hero-meta">
          {meta.token} · {meta.network}
        </p>
      </div>

      {/* Detail rows */}
      <div className="wallet-confirm-rows fade-in d2">
        <div className="wallet-confirm-row">
          <span className="wallet-confirm-label">To</span>
          <span className="wallet-confirm-value">
            {truncateAddress(address)}
            <span className="dim">Full address verified</span>
          </span>
        </div>
        <div className="wallet-confirm-row">
          <span className="wallet-confirm-label">Network</span>
          <span className="wallet-confirm-value">{meta.network}</span>
        </div>
        <div className="wallet-confirm-row">
          <span className="wallet-confirm-label">Network fee</span>
          <span className="wallet-confirm-value fee">
            Paid in TRX
            <span className="dim">Deducted automatically (up to ~{TRX_FEE_RESERVE} TRX)</span>
          </span>
        </div>
        <div className="wallet-confirm-row">
          <span className="wallet-confirm-label">Remaining</span>
          <span className="wallet-confirm-value">
            {remaining} {meta.token}
          </span>
        </div>
      </div>

      <p className="wallet-confirm-warning fade-in d2">
        Crypto transactions are irreversible. Verify the recipient address before confirming.
      </p>

      <div className="fade-in d3">
        <button type="button" className="btn-primary" onClick={onConfirm} disabled={sending}>
          {sending ? 'Sending…' : 'Confirm Send'}
        </button>
        <div style={{ height: 10 }} />
        <button type="button" className="btn-tertiary" onClick={onBack} disabled={sending}>
          Cancel
        </button>
      </div>
    </main>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const WalletSendPage = (): ReactNode => {
  const navigate = useNavigate();
  const { currency } = walletSendRoute.useSearch();
  const [amount, setAmount] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [step, setStep] = useState<1 | 2>(1);
  const qc = useQueryClient();
  const stepUp = useStepUpGate();

  // Read cached balance — no extra fetch; vault.tsx populates this key. The
  // select subtracts any in-flight send so the max-sendable check reflects it.
  const balancesQ = useQuery({
    queryKey: ['wallet-balances'],
    queryFn: ({ signal }) => getWalletBalances(signal),
    staleTime: 30_000,
    select: applyPendingSends,
  });
  const available = balancesQ.data?.balances.find((b) => b.currency === currency)?.amount ?? '0';

  // The confirmation screen used to be a screen and not a check — the button
  // called the endpoint directly. Now the send is bound to this currency, this
  // destination and this amount: a confirmation for one cannot pay for another.
  const sendM = useMutation({
    mutationFn: () => {
      const toAddress = address.trim();
      return stepUp.guard(
        {
          action: STEP_UP_ACTIONS.VAULT_WITHDRAWAL,
          params: { currency, toAddress, amount },
          summary: `Send ${amount} ${currencyMeta(currency).token} to ${toAddress}.`,
          onSetUpBiometrics: (): void => {
            void navigate({ to: '/security' });
          },
        },
        (grant) => sendWallet(currency, toAddress, amount, grant),
      );
    },
    onSuccess: () => {
      // The provider's balance only reflects the send once it settles on-chain
      // (seconds to minutes), so refetching it now would just return the
      // pre-send figure. Instead record the send locally: applyPendingSends
      // subtracts it from the displayed balance until the provider's own figure
      // drops. Baseline is the raw (unadjusted) cached balance at this instant.
      const raw = qc.getQueryData<WalletBalancesResponse>(['wallet-balances']);
      const providerAmount = raw?.balances.find((b) => b.currency === currency)?.amount ?? '0';
      registerPendingSend(currency, amount, providerAmount);
      // History does record the send immediately (as PENDING), so refresh it.
      void qc.invalidateQueries({ queryKey: ['wallet-txns', currency] });
      showToast('Transaction submitted');
      window.setTimeout(() => {
        void navigate({ to: '/vault', search: { currency } });
      }, 800);
    },
    onError: (e: unknown) => {
      // A user who closed the confirmation sheet chose not to send; saying
      // "send failed" would report their own decision back to them as a fault.
      if (isStepUpCancelled(e)) return;
      showToast(e instanceof Error ? e.message : 'Send failed — please try again');
    },
  });

  // Redirect if arrived with no currency param (shouldn't happen via normal nav).
  if (currency === '') {
    void navigate({ to: '/vault' });
    return null;
  }

  const onPaste = (): void => {
    if (typeof navigator === 'undefined' || navigator.clipboard === undefined) {
      showToast('Clipboard not available');
      return;
    }
    void navigator.clipboard
      .readText()
      .then((text) => {
        const trimmed = text.trim();
        if (trimmed.length > 0) setAddress(trimmed);
      })
      .catch(() => {
        showToast('Could not read clipboard');
      });
  };

  const onReview = (): void => {
    if (!isValidAmount(amount) || !isValidAddress(address)) return;
    if (amountError(currency, amount, available) !== null) return;
    setStep(2);
  };

  const goBack = (): void => {
    if (step === 2) {
      setStep(1);
    } else {
      void navigate({ to: '/vault', search: { currency } });
    }
  };

  if (step === 2) {
    return (
      <>
        {stepUp.sheet}
        <Step2
          currency={currency}
          amount={amount}
          address={address}
          balance={available}
          onConfirm={(): void => {
            sendM.mutate();
          }}
          onBack={goBack}
          sending={sendM.isPending}
        />
      </>
    );
  }

  return (
    <Step1
      currency={currency}
      available={available}
      amount={amount}
      address={address}
      onAmountKey={(k): void => {
        setAmount((prev) => applyKey(prev, k));
      }}
      onPaste={onPaste}
      onAddressChange={(v): void => {
        setAddress(v);
      }}
      onNext={(): void => {
        onReview();
      }}
      onBack={goBack}
    />
  );
};

export const walletSendRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vault/send',
  component: WalletSendPage,
  validateSearch: (s: Record<string, unknown>): WalletSendSearch => ({
    currency: typeof s.currency === 'string' ? s.currency : '',
  }),
});
